import { effectiveHostname, isLocalHostname, type AppEnv } from "@how/app-kit";
import { churchFromPath, HANDOFF_PATH, legacyStaffForward, parseHost } from "@how/tenancy";

export interface BootLocation {
  readonly hostname: string;
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export interface StaffDashboardBoot {
  /** Where the browser goes instead of starting the app (an old address moving to the staff door), or null. */
  readonly forward: string | null;
  /** The router's basename: "/<slug>" on the staff door once a church is in the path, otherwise "". */
  readonly basename: string;
}

/**
 * What the staff dashboard does with the address it was opened on (ADR 0065), before anything renders. Old addresses
 * (`<slug>.dash.`, `login.`, `app.`) forward to the staff door only when VITE_HOW_STAFF_DOOR_FORWARD is on; with it
 * off they keep working as before (the church from the host, basename ""). Localhost never forwards: there the dev
 * host variable stands in for the real host. A hand-off arriving on an old address (the continue path with a
 * `#handoff=` fragment) is never forwarded: the continue page renders where it arrived, so a code for another host
 * is refused there with the generic message instead of travelling on in the address.
 */
export function bootStaffDashboard(loc: BootLocation, env: AppEnv): StaffDashboardBoot {
  const base = env.VITE_HOW_PLATFORM_BASE_DOMAIN;
  const parsed = parseHost(effectiveHostname(env, loc.hostname), base);
  const arrivingHandoff = loc.pathname === HANDOFF_PATH && /^#(?:.*&)?handoff=/.test(loc.hash);
  const forward =
    env.VITE_HOW_STAFF_DOOR_FORWARD && !isLocalHostname(loc.hostname) && !arrivingHandoff
      ? legacyStaffForward(parsed, base, loc)
      : null;
  if (forward !== null) return { forward, basename: "" };
  const church =
    parsed.kind === "central" && parsed.app === "staff_door" ? churchFromPath(loc.pathname) : null;
  return { forward: null, basename: church === null ? "" : `/${church.slug}` };
}
